/**
 * Mixin for custom elements to implement `parsedCallback` method.
 */
const ParsedCallback = (superclass) => {
  /** @param {Node} el */
  function isParsed(el) {
    // If the document is fully loaded, the component is parsed
    if (el.ownerDocument.readyState === "complete") {
      return true;
    }
    do {
      if (el.nextSibling) return true;
    } while ((el = el.parentNode));
    return false;
  }

  // Extend the class since we need to tap into the connectedCallback().
  return class extends superclass {
    connectedCallback() {
      super.connectedCallback();

      // Check if component defines a parsedCallback() method
      if (typeof this["parsedCallback"] !== "function") {
        return;
      }
      // Bind the parsedCallback() method to the component
      const parsedCallback = this["parsedCallback"].bind(this);

      // Check if component is already parsed
      if (isParsed(this)) {
        this.loggingService.log("Feed already parsed");
        parsedCallback();
        return;
      }

      // Create a mutation observer to check if the component is parsed
      this.loggingService.log("Feed not yet parsed");
      const mutationObserver = new MutationObserver(() => {
        // Check if component is parsed
        if (isParsed(this)) {
          this.loggingService.log("Feed siblings mutated, parsed");
          mutationObserver.disconnect();
          parsedCallback();
        } else {
          this.loggingService.log("Feed siblings mutated, not parsed");
        }
      });

      // Observe the parent node for changes to the child list
      mutationObserver.observe(this.parentNode, {
        childList: true,
      });

      // Disconnect if the component is disconnected before being parsed
      this.disconnectSignal.addEventListener("abort", () => {
        mutationObserver.disconnect();
      });
    }
  };
};

export { ParsedCallback };
